/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Alert} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import {AdornmentType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/AdornmentType";
import {QTableSection} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableSection";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {Formik, useFormikContext} from "formik";
import QContext from "QContext";
import {FormikValueTracker} from "qqq/components/forms/formAdjustersAndRules";
import QRecordSidebar from "qqq/components/misc/RecordSidebar";
import DashboardWidgets from "qqq/components/widgets/DashboardWidgets";
import RecordFieldJumpModal from "qqq/pages/records/RecordFieldJumpModal";
import RecordScreenContext from "qqq/pages/records/RecordScreenContext";
import RecordScreenSection from "qqq/pages/records/RecordScreenSection";
import {UseRecordScreenResult} from "qqq/pages/records/useRecordScreen";
import {sanitizeId} from "qqq/utils/qqqIdUtils";
import React, {useCallback, useContext, useEffect, useRef, useMemo, useState, memo} from "react";
import * as Yup from "yup";


//////////////////////////////////////////////////////////////////////////////////
// field-jump timing (ms): wait for a collapsed section's expand animation to   //
// settle before scrolling (else we scroll to a moving target - jerky); let the //
// smooth scroll land before starting the highlight; keep the highlight up long //
// enough to be clearly seen once the field is in view.                         //
//////////////////////////////////////////////////////////////////////////////////
const FIELD_JUMP_EXPAND_SETTLE_MS = 320;
const FIELD_JUMP_SCROLL_SETTLE_MS = 100;
const FIELD_JUMP_PULSE_MS = 1600;


/***************************************************************************
 ** Memoized read-only widget section that does NOT consume RecordScreenContext,
 ** so it won't re-render when mode or other context values change.
 ***************************************************************************/
interface ReadOnlyWidgetSectionProps
{
   section: QTableSection;
   record: QRecord;
   tableName: string;
   primaryKeyField: string;
   widgetMetaData: any;
   widgetReloadCounter: number;
   widgetRefetchSignal?: number;
   childUrlParams?: string;
   isOpen: boolean;
   onToggleCollapse?: () => void;
}

const ReadOnlyWidgetSection = memo(function ReadOnlyWidgetSection({section, record, tableName, primaryKeyField, widgetMetaData, widgetReloadCounter, widgetRefetchSignal, childUrlParams}: ReadOnlyWidgetSectionProps): JSX.Element
{
   if (section.collapsible)
   {
      widgetMetaData.collapsible = section.collapsible;
   }

   return (
      <Box width="100%" height="100%" flexGrow={1} alignItems="stretch" sx={{"& .widget.inCard": {padding: "24px"}}}>
         <DashboardWidgets
            key={`${section.name}-${record?.values?.get("id") ?? "new"}-${widgetReloadCounter}`}
            tableName={tableName}
            widgetMetaDataList={[widgetMetaData]}
            record={record}
            entityPrimaryKey={record?.values?.get(primaryKeyField)}
            omitWrappingGridContainer={true}
            childUrlParams={childUrlParams}
            widgetRefetchSignal={widgetRefetchSignal}
            screen="recordView"
         />
      </Box>
   );
}, (prev: ReadOnlyWidgetSectionProps, next: ReadOnlyWidgetSectionProps) =>
{
   return (
      prev.section === next.section
      && prev.record === next.record
      && prev.widgetReloadCounter === next.widgetReloadCounter
      && prev.widgetRefetchSignal === next.widgetRefetchSignal
      && prev.childUrlParams === next.childUrlParams
      && prev.isOpen === next.isOpen
      // onToggleCollapse is excluded — recreated each render but functionally stable
   );
});


interface RecordScreenBodyProps
{
   screen: UseRecordScreenResult;
   formId?: string;
   onSubmit: (values: any, actions: any) => Promise<void>;
   formikSubmitRef?: React.MutableRefObject<() => void>;
   renderT1Card?: () => JSX.Element;
   renderBottomBar?: (isSubmitting: boolean) => JSX.Element;
   showSidebar?: boolean;
   widgetReloadCounters?: Record<string, number>;
   widgetReloadAdditionalParams?: Record<string, { [key: string]: any }>;
   widgetRefetchSignals?: Record<string, number>;
   enterEditMode?: (fieldName?: string) => void;
   openFieldJumpRef?: React.MutableRefObject<(() => void) | null>;
}


/***************************************************************************
 ** Shared Formik-wrapped body used by both full-page RecordScreen and
 ** RecordScreenModal.  Handles context, value tracking, sections, alerts.
 ***************************************************************************/
export default function RecordScreenBody({screen, formId, onSubmit, formikSubmitRef, renderT1Card, renderBottomBar, showSidebar = true, widgetReloadCounters = {}, widgetReloadAdditionalParams = {}, widgetRefetchSignals = {}, enterEditMode, openFieldJumpRef}: RecordScreenBodyProps): JSX.Element
{
   const {
      mode, record, tableMetaData, metaData,
      tableSections, t1Section, nonT1Sections,
      formFieldsBySection, initialValues, formValidations,
      fieldRules,
      errorMessage, successMessage, warningMessage, notAllowedError,
      handleFieldChange, handleFieldBlur, reloadWidget,
      collapsibleSectionOpenStates, toggleCollapsibleSectionOpenState, openCollapsedSectionsWithErrorFields,
      setSuccessMessage, setWarningMessage, setErrorMessage,
      setFieldValueRef, formValuesRef,
      childListWidgetData,
      openAddChildRecord, openEditChildRecord, deleteChildRecord, bulkAppendChildRecords,
      tableVariant,
      sectionVisibility,
      addSubValidations,
   } = screen;

   const isEditing = mode === "edit" || mode === "create";
   const tableNameForId = tableMetaData ? sanitizeId(tableMetaData.name) : "";

   // formik JSON ref for change detection
   const formValuesJSONRef = useRef<string>("");

   // Stable wrapper for setFieldValue
   const stableSetFieldValue = useCallback((name: string, value: any, shouldValidate?: boolean) =>
   {
      setFieldValueRef.current?.(name, value, shouldValidate);
   }, []);

   const isFormDisabled = !!notAllowedError;

   const {accentColorLight} = useContext(QContext);
   const [fieldJumpModalOpen, setFieldJumpModalOpen] = useState(false);
   const [pulsingFieldName, setPulsingFieldName] = useState<string | null>(null);

   ///////////////////////////////////////////////////////////////////////////
   // ids of the deferred timeouts a single jump chains (expand-settle ->   //
   // scroll-settle -> pulse-clear).  tracked so a new jump - or an unmount //
   // mid-jump - can cancel a prior jump's pending work.                    //
   ///////////////////////////////////////////////////////////////////////////
   const jumpTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
   const clearJumpTimeouts = useCallback(() =>
   {
      jumpTimeoutsRef.current.forEach((id) => clearTimeout(id));
      jumpTimeoutsRef.current = [];
   }, []);

   //////////////////////////////////////////////////////////////////////////////
   // field names actually rendered on the current screen (visible sections'   //
   // fieldNames).  fields not rendered have no DOM node to scroll to, so they //
   // are hidden from the jump modal.                                          //
   //////////////////////////////////////////////////////////////////////////////
   const onScreenFieldNames = useMemo(() =>
   {
      const names = new Set<string>();
      for (const section of (tableSections ?? []))
      {
         if (sectionVisibility[section.name] === false)
         {
            continue;
         }
         ///////////////////////////////////////////////////////////////////////////
         // skip sections hidden via metadata (section.isHidden) - like the       //
         // RecordSidebar does.  a hidden section renders no field DOM nodes; its //
         // fields may live there only to be surfaced elsewhere (e.g. CTL's       //
         // "hiddenWorkflowFields" section, shown via the workflow widget), so    //
         // they'd be dead jump targets.                                          //
         ///////////////////////////////////////////////////////////////////////////
         if (section.isHidden)
         {
            continue;
         }
         for (const fieldName of (section.fieldNames ?? []))
         {
            const fieldMeta = tableMetaData?.fields?.get(fieldName) ?? tableMetaData?.virtualFields?.get(fieldName);

            ///////////////////////////////////////////////////////////////////////////
            // skip fields hidden at runtime (an on-load form adjuster can do        //
            // table.fields.set(name, ...isHidden) - see useRecordScreen             //
            // formAdjustersAndRules): RecordScreenSection renders no DOM node for a //
            // hidden field, so offering it here would be a dead jump target.        //
            ///////////////////////////////////////////////////////////////////////////
            if (fieldMeta?.isHidden)
            {
               continue;
            }

            /////////////////////////////////////////////////////////////////////////
            // skip WIDGET-adorned fields: RecordScreenField renders these via     //
            // FieldValueAsWidget / DynamicFormFieldAsWidget, which emit no        //
            // data-field-name node - so there's nothing to scroll to (this is the //
            // "virtual field rendered as a widget" case).                         //
            /////////////////////////////////////////////////////////////////////////
            if (fieldMeta?.hasAdornment(AdornmentType.WIDGET))
            {
               continue;
            }
            names.add(fieldName);
         }
      }

      /////////////////////////////////////////////////////////////////////////////
      // also include any of THIS table's fields that some widget has rendered   //
      // with a data-field-name node (e.g. a FIELD_VALUE_LIST widget surfacing   //
      // fields from a hidden section - CTL's workflow fields).  metadata can't  //
      // tell us which fields a widget shows, but the live DOM can, and the      //
      // modal opens over the fully-rendered record.  scoped to names that exist //
      // on this table so we don't scoop up child/joined widget rows.            //
      /////////////////////////////////////////////////////////////////////////////
      if (fieldJumpModalOpen && typeof document !== "undefined")
      {
         document.querySelectorAll("[data-field-name]").forEach((el) =>
         {
            const name = el.getAttribute("data-field-name");
            if (name && (tableMetaData?.fields?.has(name) || tableMetaData?.virtualFields?.has(name)))
            {
               names.add(name);
            }
         });
      }

      return names;
      //////////////////////////////////////////////////////////////////////////////
      // recompute when the modal opens (fieldJumpModalOpen): an on-load adjuster //
      // can hide a field by mutating tableMetaData.fields in place (no dep       //
      // reference change), and the widget-rendered DOM scan above needs the      //
      // record to be present - snapshotting at open keeps the list correct.      //
      //////////////////////////////////////////////////////////////////////////////
   }, [tableSections, sectionVisibility, tableMetaData, fieldJumpModalOpen]);

   const fieldJumpNamesToHide = useMemo(() =>
   {
      if (!tableMetaData)
      {
         return [] as string[];
      }
      //////////////////////////////////////////////////////////////////////////
      // candidates = this table's real AND virtual fields; hide any that     //
      // aren't on-screen.  virtual fields must be in the candidate set (and  //
      // the modal passes includeVirtualFields="all") or an on-screen virtual //
      // field would never make it into the jump list.                        //
      //////////////////////////////////////////////////////////////////////////
      const candidateNames = [
         ...tableMetaData.fields.keys(),
         ...(tableMetaData.virtualFields ? [...tableMetaData.virtualFields.keys()] : []),
      ];
      return candidateNames.filter((name) => !onScreenFieldNames.has(name));
   }, [tableMetaData, onScreenFieldNames]);

   /***************************************************************************
    ** Expand the field's collapsed section (if any), scroll it into view, and
    ** pulse-highlight it - the Record-Screen analog of the Query Screen's
    ** locateColumnInGrid.
    ***************************************************************************/
   const jumpToField = useCallback((fieldName: string) =>
   {
      setFieldJumpModalOpen(false);

      /////////////////////////////////////////////////////////////////////////////
      // cancel any in-flight jump's deferred work first, so a rapid second jump //
      // doesn't have an earlier jump's pulse-clear fire mid-way and cut its     //
      // highlight short.                                                        //
      /////////////////////////////////////////////////////////////////////////////
      clearJumpTimeouts();

      const owningSection = (nonT1Sections ?? []).find((s: QTableSection) => (s.fieldNames ?? []).includes(fieldName));
      const needsExpand = !!owningSection && collapsibleSectionOpenStates[owningSection.name] === false;
      if (needsExpand)
      {
         toggleCollapsibleSectionOpenState(owningSection.name);
      }

      ///////////////////////////////////////////////////////////////////////////
      // if we expanded a collapsed section, wait for its expand animation to  //
      // finish before scrolling, so we scroll to the field's settled position //
      // rather than chasing a moving target (which felt jerky).               //
      ///////////////////////////////////////////////////////////////////////////
      jumpTimeoutsRef.current.push(setTimeout(() =>
      {
         const el = document.querySelector(`[data-field-name="${fieldName}"]`);
         if (!el)
         {
            return;
         }
         ///////////////////////////////////////////////////////////////////////
         // instant (not smooth) scroll: the app sets scroll-behavior: smooth //
         // globally, so an animated scrollIntoView here raced the section-   //
         // expand + modal-close relayout and came out jittery.  "instant"    //
         // (not "auto", which would inherit the CSS smooth) forces a clean   //
         // one-shot jump for just this call.  ("instant" is valid per the    //
         // CSSOM-View spec but missing from this TS lib's ScrollBehavior     //
         // union, so cast through unknown.)                                  //
         ///////////////////////////////////////////////////////////////////////
         el.scrollIntoView({behavior: "instant" as unknown as ScrollBehavior, block: "center"});

         ////////////////////////////////////////////////////////////////////////
         // in edit mode, focus the field's input (it carries id={fieldName}). //
         // preventScroll so focusing doesn't trigger a second scroll.         //
         ////////////////////////////////////////////////////////////////////////
         if (isEditing)
         {
            document.getElementById(fieldName)?.focus({preventScroll: true});
         }

         /////////////////////////////////////////////////////////////////////////
         // start the highlight only after the scroll has brought the field in  //
         // view, so its fade is fully visible in place (not spent mid-scroll). //
         /////////////////////////////////////////////////////////////////////////
         jumpTimeoutsRef.current.push(setTimeout(() =>
         {
            setPulsingFieldName(fieldName);
            jumpTimeoutsRef.current.push(setTimeout(() => setPulsingFieldName(null), FIELD_JUMP_PULSE_MS));
         }, FIELD_JUMP_SCROLL_SETTLE_MS));
      }, needsExpand ? FIELD_JUMP_EXPAND_SETTLE_MS : 0));
   }, [isEditing, nonT1Sections, collapsibleSectionOpenStates, toggleCollapsibleSectionOpenState, clearJumpTimeouts]);

   /////////////////////////////////////////////////////////////////////////////
   // on unmount, cancel any pending jump timeouts so their deferred setState //
   // calls don't run after the component is gone.                            //
   /////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      return () =>
      {
         clearJumpTimeouts();
      };
   }, [clearJumpTimeouts]);

   /////////////////////////////////////////////////////////////////////////////
   // let the RecordScreen keydown handler (which owns the "," shortcut) open //
   // the field-jump modal via a ref, mirroring the enterEditModeRef pattern. //
   /////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (openFieldJumpRef)
      {
         openFieldJumpRef.current = () => setFieldJumpModalOpen(true);
         return () =>
         {
            openFieldJumpRef.current = null;
         };
      }
   }, [openFieldJumpRef]);

   const contextValue = useMemo(() => ({
      mode,
      tableMetaData,
      metaData,
      record,
      setFieldValue: stableSetFieldValue,
      handleFieldBlur,
      reloadWidget,
      fieldRules,
      isFormDisabled,
      addSubValidations,
      childListWidgetData,
      openAddChildRecord,
      openEditChildRecord,
      deleteChildRecord,
      bulkAppendChildRecords,
      onEditIconClick: enterEditMode,
      //////////////////////////////////////////////////////////////////////////
      // Child row-edit hosts (RecordGridWidget) call this when their on-load //
      // adjuster returns isFormDisabled.  They exit edit mode on their end   //
      // and delegate the user-facing message to the parent's page-level      //
      // error alert (red — formDisabled is a blocker, not an fyi).  The      //
      // onChildRowEditStart callback clears the alert when the user enters   //
      // edit mode on a new row so a stale formDisabled message from an       //
      // earlier session doesn't linger after a fresh attempt.                //
      //////////////////////////////////////////////////////////////////////////
      onChildFormDisabled: setErrorMessage,
      onChildRowEditStart: () => setErrorMessage(null),
      onChildWarning: setWarningMessage,
   }), [mode, tableMetaData, metaData, record, stableSetFieldValue, reloadWidget, fieldRules, isFormDisabled, addSubValidations, childListWidgetData, enterEditMode, setErrorMessage, setWarningMessage]);


   const renderAlerts = () => (
      <>
         {successMessage && (
            <Alert color="success" sx={{mb: 3, zIndex: 99, position: "sticky", top: "12px"}} onClose={() => setSuccessMessage(null)}>
               {successMessage}
            </Alert>
         )}
         {warningMessage && (
            <Alert color="warning" sx={{mb: 3, zIndex: 99, position: "sticky", top: "12px"}} icon={<Icon>warning</Icon>} onClose={() => setWarningMessage(null)}>
               {warningMessage}
            </Alert>
         )}
         {errorMessage && (
            <Alert color="error" sx={{mb: 3, zIndex: 99, position: "sticky", top: "12px"}} onClose={() => setErrorMessage(null)}>
               {errorMessage}
            </Alert>
         )}
         {notAllowedError && isEditing && (
            <Alert severity="error" sx={{mb: 3}}>
               {notAllowedError}
            </Alert>
         )}
      </>
   );

   ////////////////////////////////////////////////////////////////////////////////////
   // sections named in the materialDashboard supplemental's                         //
   // recordViewSectionNamesAboveIdentity render in the content column, above the T1 //
   // (identity) card - rather than in the normal section flow below it.             //
   //                                                                                //
   // these are filtered out of nonT1Sections, so a name that is actually the T1     //
   // section would be silently dropped (rendered nowhere); MaterialDashboardTable-  //
   // MetaData.validate() guards against that by rejecting a T1 section name here.   //
   ////////////////////////////////////////////////////////////////////////////////////
   const mdbTableMetaData = tableMetaData?.supplementalTableMetaData?.get("materialDashboard");
   const aboveIdentitySectionNames: string[] = mdbTableMetaData?.recordViewSectionNamesAboveIdentity ?? [];
   const aboveIdentitySections = nonT1Sections.filter((section: QTableSection) => aboveIdentitySectionNames.includes(section.name));
   const belowIdentitySections = nonT1Sections.filter((section: QTableSection) => !aboveIdentitySectionNames.includes(section.name));

   ////////////////////////////////////////////////////////////////////////////////////
   // sidebarSections derives from tableSections (NOT nonT1Sections) so it keeps the //
   // T1 section plus the below-identity sections; only the above-identity sections  //
   // are removed (they render above the card and shouldn't get a sidebar jump-link) //
   ////////////////////////////////////////////////////////////////////////////////////
   const sidebarSections = (tableSections ?? []).filter((section: QTableSection) => !aboveIdentitySectionNames.includes(section.name));

   const renderSection = (section: QTableSection) =>
   {
      const open = collapsibleSectionOpenStates[section.name];
      const gridCols = section.widgetName
         ? (metaData?.widgets?.get(section.widgetName)?.gridColumns ?? 12)
         : (section.gridColumns ?? 12);

      const isSectionVisible = sectionVisibility[section.name] !== false;

      ////////////////////////////////////////////////////////////////
      // determine if a widget section is read-only (doesn't change //
      // rendering path between view and edit mode)                 //
      ////////////////////////////////////////////////////////////////
      let widgetIsReadOnly = false;
      if (section.widgetName && metaData)
      {
         const widget = metaData.widgets?.get(section.widgetName);
         if (widget)
         {
            const isManagedChild = widget.type === "childRecordList" && widget.defaultValues?.has("manageAssociationName");
            const isFormWidget = widget.type === "filterAndColumnsSetup" || widget.type === "pivotTableSetup" || widget.type === "dynamicForm";
            const isExplicitEdit = widget.defaultValues?.get("includeOnRecordEditScreen");
            widgetIsReadOnly = !isManagedChild && !isFormWidget && !isExplicitEdit;
         }
      }

      return (
         <Grid
            id={section.name}
            key={section.name}
            item
            lg={gridCols}
            xs={12}
            className={`form-section-wrapper ${isSectionVisible ? "is-visible" : "is-hidden"}`}
            sx={{display: "flex", alignItems: "stretch", scrollMarginTop: "100px"}}
         >
            <Box width="100%" height={open !== false ? "100%" : "auto"}>
               {widgetIsReadOnly && section.widgetName && metaData?.widgets?.get(section.widgetName) ? (
                  <ReadOnlyWidgetSection
                     section={section}
                     record={record}
                     tableName={tableMetaData.name}
                     primaryKeyField={tableMetaData.primaryKeyField}
                     widgetMetaData={metaData.widgets.get(section.widgetName)}
                     widgetReloadCounter={(widgetReloadCounters[section.widgetName] ?? 0) + (widgetReloadCounters["__all__"] ?? 0)}
                     widgetRefetchSignal={widgetRefetchSignals[section.widgetName] ?? 0}
                     childUrlParams={widgetReloadAdditionalParams[section.widgetName] ? Object.entries(widgetReloadAdditionalParams[section.widgetName]).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&") : undefined}
                     isOpen={open}
                     onToggleCollapse={() => toggleCollapsibleSectionOpenState(section.name)}
                  />
               ) : (
                  <RecordScreenSection
                     section={section}
                     mode={mode}
                     record={record}
                     formFieldsBySection={formFieldsBySection}
                     isOpen={open}
                     onToggleCollapse={() => toggleCollapsibleSectionOpenState(section.name)}
                     tableVariant={tableVariant}
                     onEditIconClick={enterEditMode}
                     widgetReloadCounters={widgetReloadCounters}
                     widgetReloadAdditionalParams={widgetReloadAdditionalParams}
                     widgetRefetchSignals={widgetRefetchSignals}
                     widgetIsReadOnly={widgetIsReadOnly}
                  />
               )}
            </Box>
         </Grid>
      );
   };

   const renderNonT1Sections = (includeT1?: boolean) =>
   {
      const sectionsToRender = includeT1 && t1Section ? [t1Section, ...belowIdentitySections] : belowIdentitySections;
      return (
         <Grid container spacing={3} pb={4}>
            {sectionsToRender.map((section: QTableSection) => renderSection(section))}
         </Grid>
      );
   };

   const aboveIdentityIsSticky: boolean = mdbTableMetaData?.recordViewSectionsAboveIdentityAreSticky === true;

   const renderAboveIdentitySections = () =>
   {
      if (aboveIdentitySections.length === 0)
      {
         return null;
      }
      /////////////////////////////////////////////////////////////////////////////
      // willChange promotes the sticky band to its own stable compositor layer, //
      // which stops its border/edges dropping out during scroll (a known        //
      // position:sticky sub-pixel repaint bug).  the repaint only misbehaves    //
      // when a page-level alert (also sticky) overlaps the band, so apply it    //
      // only while one is shown — keeping will-change transient rather than     //
      // holding a compositor layer alive for the band's whole lifetime.         //
      /////////////////////////////////////////////////////////////////////////////
      const hasPageLevelAlert = !!(successMessage || warningMessage || errorMessage);
      const stickySx = aboveIdentityIsSticky
         ? {position: "sticky", top: "-12px", zIndex: 1, ...(hasPageLevelAlert ? {willChange: "transform"} : {})}
         : {};
      return (
         <Grid container spacing={3} mb={3} sx={stickySx}>
            {aboveIdentitySections.map((section: QTableSection) => renderSection(section))}
         </Grid>
      );
   };

   const validationSchema = Yup.object().shape(formValidations);

   const effectiveFormId = formId || `record-screen-form-${tableNameForId}`;

   return (
      <>
         {renderAlerts()}

         {tableMetaData && (
            <RecordFieldJumpModal
               open={fieldJumpModalOpen}
               onClose={() => setFieldJumpModalOpen(false)}
               tableMetaData={tableMetaData}
               fieldNamesToHide={fieldJumpNamesToHide}
               onJumpToField={jumpToField}
            />
         )}

         <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
         >
            {({isSubmitting, submitForm}) =>
            {
               if (formikSubmitRef)
               {
                  formikSubmitRef.current = submitForm;
               }

               return (
                  <RecordScreenContext.Provider value={contextValue}>
                     <Box
                        component="div"
                        id={effectiveFormId}
                        sx={{
                           "@keyframes qqq-locate-pulse-kf": {
                              "0%": {backgroundColor: accentColorLight},
                              "100%": {backgroundColor: "transparent"},
                           },
                           ...(pulsingFieldName
                              ? {
                                 ///////////////////////////////////////////////////////////////////
                                 // widen the highlighted box in the X-dimension only: horizontal //
                                 // padding pushed back out by equal negative horizontal margin,  //
                                 // so the accent fill extends past the text left/right with no   //
                                 // layout shift and no vertical growth.                          //
                                 ///////////////////////////////////////////////////////////////////
                                 [`& [data-field-name="${pulsingFieldName}"]`]: {
                                    animation: `qqq-locate-pulse-kf ${FIELD_JUMP_PULSE_MS}ms ease-out`,
                                    borderRadius: "0.5rem",
                                    marginLeft: "-8px",
                                    marginRight: "-8px",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                 },
                              }
                              : {}),
                        }}
                     >
                        <FormikValueTracker
                           formValuesRef={formValuesRef}
                           formValuesJSONRef={formValuesJSONRef}
                           setFieldValueRef={setFieldValueRef}
                           onFieldChange={handleFieldChange}
                        />
                        {isEditing && <ScrollToFirstError openCollapsedSectionsWithErrorFields={openCollapsedSectionsWithErrorFields} />}

                        {showSidebar ? (
                           <Grid container spacing={3} flexWrap={{md: "nowrap"}}>
                              <Grid item xs={12} lg={3} className="recordSidebar">
                                 <QRecordSidebar tableSections={sidebarSections} sectionVisibility={sectionVisibility} />
                              </Grid>
                              <Grid item xs={12} lg={9} className="recordWithSidebar">
                                 {renderAboveIdentitySections()}

                                 {renderT1Card && (
                                    <Grid container spacing={3}>
                                       <Grid item xs={12} mb={3}>
                                          {renderT1Card()}
                                       </Grid>
                                    </Grid>
                                 )}

                                 {renderNonT1Sections()}

                                 {renderBottomBar?.(isSubmitting)}
                              </Grid>
                           </Grid>
                        ) : (
                           <>
                              {renderAboveIdentitySections()}
                              {renderT1Card && <Box mb={3}>{renderT1Card()}</Box>}
                              {renderNonT1Sections(!renderT1Card)}
                              {renderBottomBar?.(isSubmitting)}
                           </>
                        )}
                     </Box>
                  </RecordScreenContext.Provider>
               );
            }}
         </Formik>
      </>
   );
}


/***************************************************************************
 ** Scroll to the first validation error on submit.
 ***************************************************************************/
function ScrollToFirstError({openCollapsedSectionsWithErrorFields}: {openCollapsedSectionsWithErrorFields: (errorFieldNames: string[]) => void}): JSX.Element
{
   const {submitCount, isValid, errors} = useFormikContext<any>();

   useEffect(() =>
   {
      if (submitCount === 0 || isValid) return;

      const errorFieldNames = Object.keys(errors);
      if (errorFieldNames.length > 0)
      {
         openCollapsedSectionsWithErrorFields(errorFieldNames);
      }

      setTimeout(() =>
      {
         const firstError = document.querySelector("[data-field-error]");
         if (firstError)
         {
            firstError.scrollIntoView({block: "center"});
         }
      }, 150);
   }, [submitCount, isValid]);

   return null;
}
